import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cctv, ChevronsUpDown, Check } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { CommandLoading } from 'cmdk';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CustomerDto, useSearchCustomersQuery } from '@/hooks/customerHooks';
import { useSearchCustomerSitesQuery } from '@/hooks/siteHooks';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

const DeviceAddNew: React.FC = () => {
  const navigate = useNavigate();

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSelectorOpen, setCustomerSelectorOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');

  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [siteSelectorOpen, setSiteSelectorOpen] = useState(false);
  const [siteQuery, setSiteQuery] = useState('');

  const { data: customersListed, status: customersStatus } = useSearchCustomersQuery({
    query: customerQuery,
    enabled: customerSelectorOpen,
  });

  const { data: sitesListed, status: sitesStatus } = useSearchCustomerSitesQuery({
    query: siteQuery,
    customerID: selectedCustomerId,
    enabled: Boolean(selectedCustomerId),
  });

  const sites = sitesListed?.pages.flat() ?? [];

  const selectedCustomerName = customersListed?.pages
    .flat()
    .find((c: CustomerDto) => c.id === selectedCustomerId)?.name;

  const selectedSiteName = sites.find((s) => s.id === selectedSiteId)?.name;

  const canContinue = Boolean(selectedCustomerId && selectedSiteId);

  const handleContinue = () => {
    if (!canContinue) return;
    navigate(`/devices/customer/${selectedCustomerId}/add-device?siteId=${selectedSiteId}`);
  };

  return (
    <div className="mx-auto">
      <section className="mx-auto grid max-w-[1200px] mt-24 gap-8 grid-cols-1 md:grid-cols-2">
        <div className="flex items-start gap-4 mt-8 flex-col mx-4 md:mx-0">
          <div className="flex items-center gap-4">
            <Cctv className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Add a new device</h1>
          </div>
          <p className="text-muted-foreground">
            Devices are cameras and security systems that send alerts to Watchful. Select the
            customer and site you want to add this device to, then configure its connection
            settings.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border bg-[#111] text-white mx-4 md:mx-0">
          <div className="p-4 border-b space-y-1.5">
            <Label>Customer</Label>
            <Popover
              open={customerSelectorOpen}
              onOpenChange={(open) => {
                setCustomerSelectorOpen(open);
                if (open) setCustomerQuery('');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={customerSelectorOpen}
                  className="w-full justify-between text-white"
                >
                  {selectedCustomerName ?? 'Select Customer...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command filter={() => 1}>
                  <CommandInput
                    placeholder="Search customers..."
                    className="h-9"
                    onValueChange={setCustomerQuery}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {customersStatus === 'success' && 'No customer found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {customersListed?.pages.flat().map((customer: CustomerDto) => (
                        <CommandItem
                          key={customer.id}
                          value={customer.id}
                          onSelect={(value) => {
                            setSelectedCustomerId(value === selectedCustomerId ? '' : value);
                            setSelectedSiteId('');
                            setCustomerSelectorOpen(false);
                          }}
                        >
                          {customer.name}
                          <Check
                            className={cn(
                              'ml-auto',
                              selectedCustomerId === customer.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {customersStatus === 'pending' && (
                      <CommandLoading>
                        <div className="flex items-center justify-center p-2">
                          <Spinner size="small" />
                        </div>
                      </CommandLoading>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="p-4 border-b space-y-1.5">
            <Label>Site</Label>
            <Popover
              open={siteSelectorOpen}
              onOpenChange={(open) => {
                if (!selectedCustomerId) return;
                setSiteSelectorOpen(open);
                if (open) setSiteQuery('');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={siteSelectorOpen}
                  disabled={!selectedCustomerId}
                  className="w-full justify-between text-white"
                >
                  {selectedSiteName ??
                    (selectedCustomerId ? 'Select Site...' : 'Select a customer first')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command filter={() => 1}>
                  <CommandInput
                    placeholder="Search sites..."
                    className="h-9"
                    onValueChange={setSiteQuery}
                  />
                  <CommandList>
                    <CommandEmpty>{sitesStatus === 'success' && 'No sites found.'}</CommandEmpty>
                    <CommandGroup>
                      {sites.map((site) => (
                        <CommandItem
                          key={site.id}
                          value={site.id}
                          onSelect={(value) => {
                            setSelectedSiteId(value === selectedSiteId ? '' : value);
                            setSiteSelectorOpen(false);
                          }}
                        >
                          {site.name}
                          <Check
                            className={cn(
                              'ml-auto',
                              selectedSiteId === site.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {sitesStatus === 'pending' && (
                      <CommandLoading>
                        <div className="flex items-center justify-center p-2">
                          <Spinner size="small" />
                        </div>
                      </CommandLoading>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="p-4 flex justify-end">
            <Button onClick={handleContinue} disabled={!canContinue}>
              Continue
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeviceAddNew;
